/** GPX 1.1 serialization — the day-one escape hatch to every other app.
 * Pure: plan in, XML string out.
 *
 * Phone parity: the iOS importer infers kind=planned when trkpts carry no
 * <time>, which is how it tells "a route someone planned" from "a route
 * someone drove." So this writer emits NO time elements anywhere — that's
 * what makes a web-exported GPX land on the phone as an editable planned
 * trip instead of a recorded track. */

import type { ElevationSample, Plan } from "./types";
import { planGeometry } from "./types";

function escapeXML(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

function lat6(n: number): string {
  return n.toFixed(6);
}

function lon6(n: number): string {
  return n.toFixed(6);
}

/** GPX document for a plan. With samples, the track carries the densified
 * line with <ele> on every point; without, the raw plan geometry. */
export function planToGPX(plan: Plan, samples?: ElevationSample[]): string {
  const lines: string[] = [];
  lines.push('<?xml version="1.0" encoding="UTF-8"?>');
  lines.push('<gpx version="1.1" creator="Dustlark" xmlns="http://www.topografix.com/GPX/1/1">');

  for (const wpt of plan.waypoints) {
    const [lon, lat] = wpt.at;
    lines.push(
      `<wpt lat="${lat6(lat)}" lon="${lon6(lon)}"><name>${escapeXML(wpt.name)}</name></wpt>`,
    );
  }

  lines.push("<trk>");
  lines.push(`<name>${escapeXML(plan.name || "Route")}</name>`);
  lines.push("<trkseg>");

  if (samples) {
    for (const s of samples) {
      const [lon, lat] = s.at;
      lines.push(
        `<trkpt lat="${lat6(lat)}" lon="${lon6(lon)}"><ele>${s.eleM.toFixed(1)}</ele></trkpt>`,
      );
    }
  } else {
    for (const [lon, lat] of planGeometry(plan)) {
      lines.push(`<trkpt lat="${lat6(lat)}" lon="${lon6(lon)}"/>`);
    }
  }

  lines.push("</trkseg>");
  lines.push("</trk>");
  lines.push("</gpx>");

  return lines.join("\n");
}

/** "big-bend-loop.gpx" — kebab-cased plan name, safe for filesystems. */
export function gpxFilename(plan: Plan): string {
  const slug = plan.name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return `${slug || "route"}.gpx`;
}
